import { validateFormValues } from './kit-form.mjs';
import { AppSdkError, requireId, notifyObservers, disposeOwned } from './request.mjs';

const settled = new Set(['confirmed', 'refused']);
const outcomes = new Set([...settled, 'pending', 'unknown']);

// The owner adapter classifies its actual result and returns an authorized file
// value. This controller neither invents a wire DTO nor grants write access.
export function createFormController({
  fields,
  initialValues = {},
  complexValidators = {},
  submit,
  classifyResult,
  upload,
  fileValueFromResult,
  reconcile,
  onInvalidate,
}) {
  if (typeof submit !== 'function' || typeof classifyResult !== 'function')
    throw new TypeError('Form submit and producer-result classification are required');
  const declarations = structuredClone(fields);
  const known = new Map(declarations.map((field) => [field.id, field]));
  let values = structuredClone(initialValues);
  let generation = 0;
  let pending = null;
  let attempt = null;
  let disabled = false;
  let snapshot;
  const listeners = new Set();
  const emit = (state) => {
    snapshot = Object.freeze({ values: structuredClone(values), errors: {}, ...state });
    notifyObservers(listeners);
  };
  const requireEditable = () => {
    if (disabled) throw new AppSdkError('FORM_INVALIDATED');
    if (attempt && !settled.has(snapshot.status)) throw new AppSdkError('OUTCOME_PENDING');
  };
  const validate = () => validateFormValues(declarations, values, complexValidators);
  const accept = (result, ticket) => {
    if (ticket !== generation) return { status: 'superseded' };
    const status = classifyResult(result);
    if (!outcomes.has(status)) throw new AppSdkError('INVALID_FORM_OUTCOME');
    if (status === 'confirmed' && attempt.kind === 'upload') {
      const value = fileValueFromResult(result);
      const checked = validateFormValues(
        [known.get(attempt.fieldId)],
        { [attempt.fieldId]: value },
        complexValidators,
      );
      if (!checked.valid) throw new AppSdkError('INVALID_FILE_VALUE');
      values = { ...values, ...checked.values };
    }
    emit({ status, operationId: attempt.operationId, result: structuredClone(result) });
    return snapshot;
  };
  const run = async (nextAttempt, invoke) => {
    requireEditable();
    attempt = nextAttempt;
    generation++;
    const ticket = generation;
    pending = new AbortController();
    emit({ status: 'pending', operationId: attempt.operationId });
    try {
      return accept(await invoke(pending.signal), ticket);
    } catch {
      if (ticket !== generation) return { status: 'superseded' };
      emit({ status: 'unknown', operationId: attempt.operationId });
      return snapshot;
    }
  };
  const invalidate = (reason = 'context_changed') => {
    generation++;
    disabled = true;
    pending?.abort();
    values = {};
    emit({ status: 'blocked', reason, operationId: attempt?.operationId });
  };
  emit({ status: 'editing' });
  const detach = onInvalidate?.(invalidate);
  return Object.freeze({
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setField: (fieldId, value) => {
      requireEditable();
      if (!known.has(fieldId)) throw new AppSdkError('UNKNOWN_FIELD');
      generation++;
      values = { ...values, [fieldId]: structuredClone(value) };
      attempt = null;
      emit({ status: 'editing' });
    },
    unsetField: (fieldId) => {
      requireEditable();
      if (!known.has(fieldId)) throw new AppSdkError('UNKNOWN_FIELD');
      generation++;
      values = { ...values };
      delete values[fieldId];
      attempt = null;
      emit({ status: 'editing' });
    },
    validate: () => {
      const result = validate();
      emit({ ...snapshot, values: structuredClone(values), errors: result.errors });
      return result;
    },
    submit: async (operationId) => {
      requireEditable();
      requireId(operationId);
      const checked = validate();
      if (!checked.valid) {
        emit({ status: 'invalid', errors: checked.errors });
        return snapshot;
      }
      return run({ kind: 'submit', operationId }, (signal) =>
        submit({ operationId, values: checked.values, signal }),
      );
    },
    upload: async (fieldId, file, operationId) => {
      requireEditable();
      requireId(operationId);
      if (known.get(fieldId)?.type !== 'file') throw new AppSdkError('INVALID_FILE_FIELD');
      if (typeof upload !== 'function' || typeof fileValueFromResult !== 'function')
        throw new AppSdkError('UPLOAD_UNAVAILABLE');
      return run({ kind: 'upload', fieldId, operationId }, (signal) =>
        upload({ fieldId, file, operationId, signal }),
      );
    },
    reconcile: async () => {
      if (disabled) return { status: 'superseded' };
      if (!attempt || settled.has(snapshot.status)) throw new AppSdkError('NO_PENDING_OUTCOME');
      if (typeof reconcile !== 'function') throw new AppSdkError('RECONCILIATION_UNAVAILABLE');
      const ticket = generation;
      const original = structuredClone(attempt);
      const result = await reconcile(original);
      // A fresh owning VM may reconcile the original operation under current
      // authority; a retired document cannot continue calls or restore values.
      if (disabled || ticket !== generation) return { status: 'superseded' };
      return accept(result, ticket);
    },
    invalidate,
    close: () => {
      invalidate('closed');
      disposeOwned(detach);
      listeners.clear();
    },
  });
}
